import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { FilterBar } from '@/components/ui/data-table-customized'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardTitle } from '@/components/ui/card'
import { paths } from '@/utils/path'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    Server,
    Cpu,
    MemoryStick,
    HardDrive,
    Network,
    AppWindow,
    Laptop,
    Box,
    Download,
    LucideIcon,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { useItemList, resolveCategory } from '../../hooks/use-item-list'
import { getDashboardStats, getItemsExport } from '../../services'
import { exportItemsCsv } from '../../utils/csv-export'

interface CategoryTab {
    label: string
    value: string
    Icon: LucideIcon
    iconClass: string
}

const categoryTabs: CategoryTab[] = [
    {
        label: 'SERVER',
        value: 'server',
        Icon: Server,
        iconClass: 'text-blue-600',
    },
    {
        label: 'PROCESSOR',
        value: 'cpu',
        Icon: Cpu,
        iconClass: 'text-green-600',
    },
    {
        label: 'MEMORY',
        value: 'ram',
        Icon: MemoryStick,
        iconClass: 'text-purple-600',
    },
    {
        label: 'DISK',
        value: 'disk',
        Icon: HardDrive,
        iconClass: 'text-orange-600',
    },
    { label: 'GPU', value: 'card_gpu', Icon: Box, iconClass: 'text-pink-600' },
    {
        label: 'NETWORK CARD',
        value: 'card_network',
        Icon: Network,
        iconClass: 'text-cyan-600',
    },
    {
        label: 'SWITCH',
        value: 'switch',
        Icon: AppWindow,
        iconClass: 'text-indigo-600',
    },
    {
        label: 'LAPTOP',
        value: 'laptop',
        Icon: Laptop,
        iconClass: 'text-teal-600',
    },
    { label: 'MODULE', value: 'module', Icon: Box, iconClass: 'text-rose-600' },
]

const ItemsList = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const selectedCategory = searchParams.get('tab') || 'server'

    const {
        items,
        pagination,
        stateComponent,
        queryParams,
        resolvedCategory,
        control,
        setFilter,
        params,
        searchInputProps,
        columns,
        filterItems,
        filterToggles,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        resetAll,
    } = useItemList(selectedCategory)

    const [isExporting, setIsExporting] = useState(false)

    const handleCategoryChange = (newCategory: string) => {
        resetAll()
        setSearchParams({ tab: newCategory })
    }

    // Export the FULL filtered set (not just the current page), then format client-side.
    const handleExport = async () => {
        if (isExporting) return
        setIsExporting(true)
        try {
            const res = await getItemsExport({
                ...queryParams,
                category: resolvedCategory,
            })
            const rows = res.results || []
            const ok = exportItemsCsv(rows, selectedCategory)
            if (ok) {
                toast.success(`Exported ${rows.length} items`)
            } else {
                toast.error('No items to export')
            }
        } catch {
            toast.error('Failed to export items')
        } finally {
            setIsExporting(false)
        }
    }

    // Total item count per category tab (supplementary — never blocks the page)
    const { data: stats } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.WAREHOUSE_DASHBOARD],
            queryFn: getDashboardStats,
            staleTime: 5 * 60 * 1000, // 5 minutes
        },
        { showLoading: false, showError: false }
    )

    const categoryCountMap = useMemo(() => {
        const map: Record<string, number> = {}
        if (!stats) return map
        for (const [k, v] of Object.entries(stats.equipment_by_category ?? {}))
            map[k] = (map[k] ?? 0) + v
        for (const [k, v] of Object.entries(stats.components_by_category ?? {}))
            map[k] = (map[k] ?? 0) + v
        return map
    }, [stats])

    const getCategoryCount = (value: string): number =>
        resolveCategory(value)
            .split(',')
            .reduce((sum, c) => sum + (categoryCountMap[c] ?? 0), 0)

    return (
        <>
            <SubHeaders
                breadcrumbs={[
                    {
                        path: paths.warehouse,
                        title: 'Capacities & Resources',
                    },
                    {
                        path: paths.warehouseItems,
                        title: 'All Items',
                    },
                ]}
            />

            {/* Category summary cards — one row, click to switch (active = highlighted) */}
            <div className="flex gap-2 overflow-x-auto pb-1">
                {categoryTabs.map((tab) => {
                    const active = selectedCategory === tab.value
                    const { Icon } = tab
                    return (
                        <Card
                            key={tab.value}
                            onClick={() => handleCategoryChange(tab.value)}
                            className={`min-w-[110px] flex-1 cursor-pointer transition-colors hover:shadow-lg ${
                                active ? 'border-blue-200 bg-blue-50' : ''
                            }`}
                        >
                            <CardContent className="p-3">
                                <div className="flex items-start justify-between gap-1">
                                    <CardTitle
                                        className={`min-h-[2rem] text-xs font-medium uppercase leading-tight ${
                                            active ? 'text-blue-600' : ''
                                        }`}
                                    >
                                        {tab.label}
                                    </CardTitle>
                                    <Icon
                                        className={`mt-0.5 h-4 w-4 shrink-0 ${tab.iconClass}`}
                                    />
                                </div>
                                <div className="text-2xl font-bold">
                                    {stats ? getCategoryCount(tab.value) : '—'}
                                </div>
                            </CardContent>
                        </Card>
                    )
                })}
            </div>

            <Paper className="mt-2 space-y-2">
                <FilterBar
                    storageKey={`warehouse-items-${selectedCategory}`}
                    control={control}
                    setFilter={setFilter}
                    items={filterItems}
                />

                {stateComponent || (
                    <DataTableCustomized
                        columns={columns}
                        rows={items}
                        rowKey={(row) => row.id}
                        size="small"
                        toolbar={{
                            search: {
                                ...searchInputProps,
                                placeholder: 'Search items...',
                            },
                            settings: {
                                show: true,
                                storageKey: `warehouse-items-${selectedCategory}`,
                                filterToggles,
                                actionComponents: [
                                    <Button
                                        key="export-csv"
                                        variant="ghost"
                                        className="gap-2 w-full justify-start"
                                        disabled={
                                            isExporting ||
                                            (pagination?.total || 0) === 0
                                        }
                                        onClick={handleExport}
                                    >
                                        <Download className="h-4 w-4" />
                                        {isExporting
                                            ? 'Exporting...'
                                            : 'Export CSV'}
                                    </Button>,
                                ],
                            },
                        }}
                        sorting={{
                            sortBy: params.sort_by,
                            sortOrder: params.sort_order,
                            onSortChange: handleSortChange,
                        }}
                        pagination={{
                            page: params.page,
                            pageSize: params.page_size,
                            total: pagination?.total || 0,
                            onPageChange: handlePageChange,
                            onPageSizeChange: handlePageSizeChange,
                        }}
                    />
                )}
            </Paper>
        </>
    )
}

export default ItemsList
